import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { HardwareType, HardwareTypeUpdate, ModelReference, ModelReferenceCreate, typesService } from '../../../services/typesService';

export type HardwareTypeTab = 'overview' | 'config' | 'modelrefs' | 'devices' | 'history';

interface FormErrors {
  code?: string;
  name?: string;
  description?: string;
}

const apiError = (err: any, fallback: string): string => err?.response?.data?.detail || err?.message || fallback;

/**
 * Everything the hardware type dialog shows and does: the identity and
 * behaviour form, the model references (list, add, remove) and the
 * messages. Each tab reads the slice it needs.
 */
export const useHardwareTypeDetail = (hardwareType: HardwareType | null, modelRefs: ModelReference[], isOpen: boolean, onTypeUpdated: () => void) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<HardwareTypeTab>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // Identity and behaviour form
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rebootPayload, setRebootPayload] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [refAnep, setRefAnep] = useState<string[]>([]);
  const [newRefAnep, setNewRefAnep] = useState('');

  // Model references
  const [localModelRefs, setLocalModelRefs] = useState<ModelReference[]>([]);
  const [availableModelRefs, setAvailableModelRefs] = useState<ModelReference[]>([]);
  const [selectedRefCode, setSelectedRefCode] = useState('');

  const seedForm = useCallback((hw: HardwareType) => {
    setCode(hw.code);
    setName(hw.name);
    setDescription(hw.description || '');
    setRebootPayload(hw.reboot_payload || '');
    setIsActive(hw.is_active);
    setRefAnep(hw.ref_anep || []);
    setNewRefAnep('');
  }, []);

  // A new type: seed the form, forget the previous dialog state.
  useEffect(() => {
    if (!hardwareType) return;
    seedForm(hardwareType);
    setLocalModelRefs(modelRefs);
    setError(null);
    setSuccessMessage(null);
    setErrors({});
    setIsEditing(false);
    setActiveTab('overview');
    setSelectedRefCode('');
  }, [hardwareType, modelRefs, seedForm]);

  // The references offered for adding: those of every active type, loaded when the tab opens.
  useEffect(() => {
    if (!isOpen || !hardwareType || activeTab !== 'modelrefs') return;
    let cancelled = false;
    typesService.getAllModelReferences(false)
      .then((perType) => { if (!cancelled) setAvailableModelRefs(perType.flatMap(({ refs }) => refs)); })
      .catch((err) => console.error('Failed to load available model references:', err));
    return () => { cancelled = true; };
  }, [isOpen, hardwareType, activeTab]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!code.trim()) next.code = t('deviceTypes.validation.codeRequired');
    else if (!/^[A-Z0-9_-]{1,20}$/.test(code.trim())) next.code = t('deviceTypes.validation.codeFormat');
    if (!name.trim()) next.name = t('deviceTypes.validation.nameRequired');
    else if (name.trim().length > 100) next.name = t('deviceTypes.validation.nameMax');
    if (description && description.length > 500) next.description = t('deviceTypes.validation.descriptionMax');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!hardwareType) return;
    setError(null);
    setSuccessMessage(null);
    if (!validate()) return;
    setIsLoading(true);
    try {
      const updateData: HardwareTypeUpdate = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        reboot_payload: rebootPayload.trim() || undefined,
        is_active: isActive,
        ref_anep: refAnep,
      };
      await typesService.updateHardwareType(hardwareType.id, updateData);
      setSuccessMessage(t('deviceTypes.detail.updated'));
      setIsEditing(false);
      onTypeUpdated();
    } catch (err: any) {
      console.error('[HARDWARE TYPE DETAIL] Error saving:', err);
      setError(apiError(err, t('deviceTypes.detail.updateFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = () => {
    setIsEditing(true);
    setSuccessMessage(null);
  };

  const cancelEdit = () => {
    if (hardwareType) seedForm(hardwareType);
    setIsEditing(false);
    setError(null);
    setErrors({});
  };

  const addRefAnep = () => {
    const val = newRefAnep.trim();
    if (val && !refAnep.includes(val)) {
      setRefAnep([...refAnep, val]);
      setNewRefAnep('');
    }
  };

  const removeRefAnep = (index: number) => setRefAnep(refAnep.filter((_, i) => i !== index));

  /** Active references not already on this type (by code). */
  const availableRefs = (() => {
    const existing = new Set(localModelRefs.map((r) => r.code.toUpperCase()));
    return availableModelRefs.filter((ref) => !existing.has(ref.code.toUpperCase()));
  })();

  const reloadRefs = async () => {
    if (!hardwareType) return;
    setLocalModelRefs(await typesService.getModelReferences(hardwareType.id, true));
  };

  const addModelRef = async () => {
    if (!hardwareType) return;
    if (!selectedRefCode) {
      setError(t('deviceTypes.detail.refPick'));
      return;
    }
    const picked = availableModelRefs.find((r) => r.code === selectedRefCode);
    setIsLoading(true);
    setError(null);
    try {
      const data: ModelReferenceCreate = {
        code: selectedRefCode.toUpperCase(),
        name: picked?.name || `${hardwareType.name} ${selectedRefCode}`,
        description: picked?.description,
      };
      await typesService.createModelReference(hardwareType.id, data);
      await reloadRefs();
      setSelectedRefCode('');
      setSuccessMessage(t('deviceTypes.detail.refAdded'));
    } catch (err: any) {
      setError(t('deviceTypes.detail.refAddFailed', { error: apiError(err, String(err)) }));
    } finally {
      setIsLoading(false);
    }
  };

  const removeModelRef = async (refId: number) => {
    if (!hardwareType) return;
    if (!window.confirm(t('deviceTypes.detail.refRemoveConfirm'))) return;
    setIsLoading(true);
    setError(null);
    try {
      await typesService.deleteModelReference(refId);
      await reloadRefs();
      setSuccessMessage(t('deviceTypes.detail.refRemoved'));
    } catch (err: any) {
      setError(t('deviceTypes.detail.refRemoveFailed', { error: apiError(err, String(err)) }));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    hardwareType, activeTab, setActiveTab, isEditing, isLoading, error, successMessage, setSuccessMessage, errors,
    code, setCode, name, setName, description, setDescription, rebootPayload, setRebootPayload, isActive, setIsActive,
    refAnep, newRefAnep, setNewRefAnep, addRefAnep, removeRefAnep,
    localModelRefs, availableModelRefs, availableRefs, selectedRefCode, setSelectedRefCode, addModelRef, removeModelRef,
    save, startEdit, cancelEdit,
  };
};

export type HardwareTypeDetailState = ReturnType<typeof useHardwareTypeDetail>;
